import { useState, useRef, useEffect } from 'react'
import { Mic, Square, Loader2, Check, X, AlertCircle } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import { useStore } from '../store/useStore'
import { Capacitor } from '@capacitor/core'
import { SpeechRecognition } from '@capacitor-community/speech-recognition'
import ReactMarkdown from 'react-markdown'
import { useTranslation } from 'react-i18next'

interface ImageSuggestion {
  external_url: string
  source: string
  photographer: string
  photographer_url: string
  license_name: string
  license_url: string
}

interface NewProject {
  name: string
  description?: string
  color?: string
}

interface TaskSuggestion {
  title: string
  description?: string
  priority?: 'low' | 'medium' | 'high'
  startDate?: string  // ISO 8601 format from AI（開始日）
  dueDate?: string  // ISO 8601 format from AI（期限）
  projectId: string  // 各タスクに個別のプロジェクトID
  projectName?: string  // プロジェクト名（表示用）
  images?: ImageSuggestion[]  // タスクに関連する画像候補
}

interface VoiceResponse {
  transcription: string
  tasks: TaskSuggestion[]
  summary: string
  newProjects?: NewProject[]  // 新規作成するプロジェクト一覧
}

interface VoiceToTasksProps {
  compact?: boolean
}

interface PlaceResult {
  name: string
  address: string
  rating?: number
  userRatingsTotal?: number
  priceLevel?: number
  types?: string[]
  editorialSummary?: string
  googleMapsUri?: string
  websiteUri?: string
  phoneNumber?: string
  openingHours?: string
  topReview?: string
  aiEnrichedDescription?: string  // AI生成の充実した説明
}

export default function VoiceToTasks({ compact = false }: VoiceToTasksProps) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { fetchProjects, setSelectedProjectId, projects } = useStore()
  const [isRecording, setIsRecording] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [processingStep, setProcessingStep] = useState<'transcribing' | 'analyzing' | null>(null)
  const [recordingTime, setRecordingTime] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [voiceResponse, setVoiceResponse] = useState<VoiceResponse | null>(null)
  const [usageCount, setUsageCount] = useState(0)
  const [usageLimit, setUsageLimit] = useState(300)
  const [showModal, setShowModal] = useState(false)
  const [tempStorageProjectId, setTempStorageProjectId] = useState<string | null>(null)
  const [textInput, setTextInput] = useState<string>('')
  const [transcriptionText, setTranscriptionText] = useState<string>('')
  const [selectedProjectOverride, setSelectedProjectOverride] = useState<string | null>(null)
  const [newProjectName, setNewProjectName] = useState<string>('')

  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const timerRef = useRef<NodeJS.Timeout | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const speechRecognitionListenerRef = useRef<any>(null)
  const isNativeRef = useRef<boolean>(false) // 録音開始時のisNativeを保持
  const transcriptionTextRef = useRef<string>('') // リスナー用の最新transcriptionText
  // const processingAbortedRef = useRef<boolean>(false) // 処理がキャンセルされたかどうか（将来のキャンセル機能用）

  useEffect(() => {
    loadUsageCount()
  }, [user])

  // 一時保存プロジェクトのIDを取得
  useEffect(() => {
    const tempProject = projects.find(p => p.name === '一時保存')
    if (tempProject) {
      setTempStorageProjectId(tempProject.id)
    }
  }, [projects])

  // モーダルを閉じたときにストリームとSpeechRecognitionを停止
  useEffect(() => {
    if (!showModal) {
      // クリーンアップ処理
      const cleanup = async () => {
        // タイマーを停止
        if (timerRef.current) {
          clearInterval(timerRef.current)
          timerRef.current = null
        }

        // ネイティブ音声認識を停止
        const isNative = Capacitor.isNativePlatform()
        if (isNative) {
          try {
            await SpeechRecognition.stop()
          } catch (err) {
            // 既に停止している場合は無視
          }
        }

        // Web版ストリームを停止
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop())
          streamRef.current = null
        }

        // MediaRecorderを停止
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
          try {
            mediaRecorderRef.current.stop()
          } catch (err) {
            // 無視
          }
          mediaRecorderRef.current = null
        }

        // リスナーをクリーンアップ
        if (speechRecognitionListenerRef.current) {
          try {
            if (typeof speechRecognitionListenerRef.current.remove === 'function') {
              speechRecognitionListenerRef.current.remove()
            } else if (speechRecognitionListenerRef.current.partial && speechRecognitionListenerRef.current.state) {
              speechRecognitionListenerRef.current.partial.remove()
              speechRecognitionListenerRef.current.state.remove()
            }
          } catch (err) {
            // 無視
          }
          speechRecognitionListenerRef.current = null
        }
      }

      cleanup()
      setIsRecording(false)
      setTranscriptionText('')
      setIsProcessing(false)
      setProcessingStep(null)
    }
  }, [showModal])

  const requestMicrophoneAccess = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      streamRef.current = stream
    } catch (err) {
      console.error('Error accessing microphone:', err)
      setError('マイクへのアクセスに失敗しました')
    }
  }

  const loadUsageCount = async () => {
    if (!user) return

    const { data, error } = await supabase
      .from('user_profiles')
      .select('ai_voice_usage_count, subscription_tier')
      .eq('id', user.id)
      .single()

    if (error) {
      console.error('Error loading usage count:', error)
      return
    }

    if (data) {
      setUsageCount(data.ai_voice_usage_count || 0)

      // プランごとの上限を設定
      switch (data.subscription_tier) {
        case 'free':
          setUsageLimit(3)
          break
        case 'pro':
          setUsageLimit(10)
          break
        case 'premium':
          setUsageLimit(30)
          break
        case 'ai_assistant':
          setUsageLimit(300)
          break
        default:
          setUsageLimit(0)
      }
    }
  }

  // ネイティブ音声認識の開始（モバイルアプリ用）
  const startNativeSpeechRecognition = async () => {
    try {
      setError(null)
      setTranscriptionText('')
      transcriptionTextRef.current = '' // refもリセット

      // 音声認識が利用可能かチェック
      const { available } = await SpeechRecognition.available()
      if (!available) {
        throw new Error('音声認識が利用できません')
      }

      // パーミッションを要求
      try {
        await SpeechRecognition.requestPermissions()
      } catch (err) {
        throw new Error('マイクへのアクセスが許可されていません')
      }

      // 音声認識を開始
      await SpeechRecognition.start({
        language: 'ja-JP',
        maxResults: 1,
        popup: false,
        partialResults: true
      })

      setIsRecording(true)
      setRecordingTime(0)

      // リアルタイム認識結果のリスナー（部分結果）
      const partialListener = await SpeechRecognition.addListener('partialResults', (data: any) => {
        console.log('[Native] partialResults event:', JSON.stringify(data))
        if (data.matches && data.matches.length > 0) {
          const text = data.matches[0]
          console.log('[Native] partialResults text:', text)
          setTranscriptionText(text)
          transcriptionTextRef.current = text // refも更新
        }
      })

      // 音声認識の状態変化リスナー（停止時に最終結果を処理）
      // 注: listeningStateはiOSで発火しない場合があるため、stopRecordingで直接処理する
      const stateListener = await SpeechRecognition.addListener('listeningState', async (data: any) => {
        console.log('[Native] Listening state changed:', JSON.stringify(data))
        // listeningStateイベントでは処理せず、stopRecordingで処理する
      })

      // Android用: 最終結果のリスナー（Androidでは'result'イベントで最終結果が返る場合がある）
      let resultListener: any = null
      try {
        resultListener = await SpeechRecognition.addListener('result' as any, (data: any) => {
          console.log('[Native] result event:', JSON.stringify(data))
          if (data.matches && data.matches.length > 0) {
            const text = data.matches[0]
            console.log('[Native] result text:', text)
            setTranscriptionText(text)
            transcriptionTextRef.current = text
          }
        })
      } catch (e) {
        console.log('[Native] result listener not available (iOS)')
      }

      speechRecognitionListenerRef.current = { partial: partialListener, state: stateListener, result: resultListener }

      // タイマー開始
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          const newTime = prev + 1

          // 60秒で自動停止
          if (newTime >= 60) {
            stopRecording()
          }

          return newTime
        })
      }, 1000)

    } catch (err: any) {
      console.error('Error starting native speech recognition:', err)
      setError(err.message || '音声認識の開始に失敗しました')
    }
  }

  // Web版音声録音の開始（ブラウザ用）
  const startWebRecording = async () => {
    try {
      setError(null)

      // 既存のストリームを使用、なければ取得
      if (!streamRef.current) {
        await requestMicrophoneAccess()
        if (!streamRef.current) {
          setError('マイクへのアクセスに失敗しました')
          return
        }
      }

      // 音声圧縮設定：ビットレートを下げてファイルサイズを削減
      const mediaRecorder = new MediaRecorder(streamRef.current, {
        mimeType: 'audio/webm;codecs=opus',
        audioBitsPerSecond: 16000  // 16kbps（デフォルトの1/4、音声認識には十分）
      })

      mediaRecorderRef.current = mediaRecorder
      chunksRef.current = []

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data)
        }
      }

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(chunksRef.current, { type: 'audio/webm' })

        // 音声認識を実行
        setIsProcessing(true)
        setProcessingStep('transcribing')

        try {
          // BlobをBase64に変換
          const reader = new FileReader()
          const base64Promise = new Promise<string>((resolve) => {
            reader.onloadend = () => {
              const base64 = (reader.result as string).split(',')[1]
              resolve(base64)
            }
          })
          reader.readAsDataURL(audioBlob)
          const base64Audio = await base64Promise

          // Whisper APIで音声認識のみ実行
          const { data, error } = await supabase.functions.invoke('voice-to-tasks', {
            body: {
              audioBlob: base64Audio,
              transcribeOnly: true,  // 音声認識のみ
              userId: user?.id
            }
          })

          if (error) {
            console.error('Transcription error:', error)
            throw error
          }

          if (data?.error) {
            console.error('Transcription response error:', data)
            throw new Error(data.error)
          }

          // 認識されたテキストを取得
          const recognizedText = data.text || data.transcription || ''

          if (!recognizedText) {
            throw new Error('音声を認識できませんでした')
          }

          // 認識結果を表示
          setTranscriptionText(recognizedText)
          setIsProcessing(false)
          setProcessingStep(null)

          // テキストをprocessTextに渡す（テキスト入力と同じロジック）
          await processText(recognizedText)

        } catch (err: any) {
          console.error('Error in voice recognition:', err)
          setError(err.message || '音声認識に失敗しました')
          setIsProcessing(false)
          setProcessingStep(null)
        }
      }

      mediaRecorder.start()
      setIsRecording(true)
      setRecordingTime(0)

      // タイマー開始
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          const newTime = prev + 1

          // 60秒で自動停止
          if (newTime >= 60) {
            stopRecording()
          }

          return newTime
        })
      }, 1000)

    } catch (err) {
      console.error('Error starting recording:', err)
      setError('マイクへのアクセスに失敗しました')
    }
  }

  const startRecording = async () => {
    // 利用可能かチェック
    if (usageCount >= usageLimit) {
      setError('月間の利用上限に達しました。来月までお待ちください。')
      return
    }

    const isNative = Capacitor.isNativePlatform()
    isNativeRef.current = isNative // 開始時のisNativeを保持

    if (isNative) {
      // ネイティブアプリ: SpeechRecognition API
      await startNativeSpeechRecognition()
    } else {
      // Web版: MediaRecorder + Whisper API
      await startWebRecording()
    }
  }

  const stopRecording = async () => {
    console.log('[stopRecording] Called! isRecording:', isRecording)
    if (!isRecording) {
      console.log('[stopRecording] Returning early - isRecording is false')
      return
    }

    // 開始時に保存したisNativeを使用（途中で変わることがあるため）
    const isNative = isNativeRef.current
    console.log('[stopRecording] isNative:', isNative, 'transcriptionTextRef.current:', transcriptionTextRef.current)

    // まず状態を更新（UIの応答性向上のため）
    setIsRecording(false)

    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }

    try {
      console.log('[stopRecording] Entered try block, isNative:', isNative)
      if (isNative) {
        // ネイティブアプリ: SpeechRecognitionを停止
        // 注意: Androidでは既にlisteningState:stoppedの場合、stop()がハングすることがある
        // そのためタイムアウト付きで呼び出す
        console.log('[stopRecording] About to call SpeechRecognition.stop() with timeout')
        try {
          await Promise.race([
            SpeechRecognition.stop(),
            new Promise((_, reject) => setTimeout(() => reject(new Error('stop() timeout')), 1000))
          ])
          console.log('[stopRecording] SpeechRecognition.stop() completed')
        } catch (stopErr: any) {
          console.log('[stopRecording] SpeechRecognition.stop error (ignored):', stopErr?.message || stopErr)
        }

        // Androidでは listeningState: stopped が partialResults より先に来ることがあるため、
        // 少し待ってから結果を取得する
        console.log('[stopRecording] Waiting 500ms for final results...')
        await new Promise(resolve => setTimeout(resolve, 500))
        console.log('[stopRecording] 500ms wait completed')

        // 認識結果をprocessTextに渡す（refを使用して最新の値を取得）
        const currentText = transcriptionTextRef.current
        console.log('[Native] Recognized text from ref:', currentText, 'length:', currentText?.length)

        // リスナーをクリーンアップ（結果取得後に行う）
        if (speechRecognitionListenerRef.current) {
          try {
            if (typeof speechRecognitionListenerRef.current.remove === 'function') {
              // 古い形式（単一リスナー）
              speechRecognitionListenerRef.current.remove()
            } else {
              // 新しい形式（複数リスナー）
              if (speechRecognitionListenerRef.current.partial) {
                speechRecognitionListenerRef.current.partial.remove()
              }
              if (speechRecognitionListenerRef.current.state) {
                speechRecognitionListenerRef.current.state.remove()
              }
              if (speechRecognitionListenerRef.current.result) {
                speechRecognitionListenerRef.current.result.remove()
              }
            }
          } catch (listenerErr) {
            console.log('Listener cleanup error (ignored):', listenerErr)
          }
          speechRecognitionListenerRef.current = null
        }

        if (currentText) {
          await processText(currentText)
        } else {
          setError('音声が認識できませんでした')
        }
      } else {
        // Web版: MediaRecorderを停止（onstopが自動的に呼ばれる）
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
          mediaRecorderRef.current.stop()
        }
      }

      console.log('[stopRecording] isNative:', isNative, 'transcriptionTextRef:', transcriptionTextRef.current)
    } catch (err: any) {
      console.error('Error stopping recording:', err)
      setError(err.message || '録音の停止に失敗しました')
    }
  }

  // processAudio関数は削除（Web版は音声認識後にprocessTextを呼ぶように変更）

  const processText = async (text: string) => {
    setIsProcessing(true)
    setProcessingStep('analyzing')
    setError(null)

    try {
      // 利用可能かチェック
      if (usageCount >= usageLimit) {
        setError('月間の利用上限に達しました。来月までお待ちください。')
        setIsProcessing(false)
        setProcessingStep(null)
        return
      }

      console.log('[processText] Input text:', text)
      console.log('[processText] Text length:', text.length)

      // AIでタスク解析
      const { data, error } = await supabase.functions.invoke('voice-to-tasks', {
        body: {
          text: text,
          userId: user?.id
        }
      })

      if (error) {
        console.error('Edge Function error:', error)
        throw error
      }

      // データにエラーが含まれている場合
      if (data?.error) {
        console.error('Response error:', data)
        throw new Error(data.error + (data.details ? `: ${data.details}` : ''))
      }

      console.log('AI Response:', data)
      console.log('AI Response (full JSON):', JSON.stringify(data, null, 2))
      console.log('[processText] requestType:', data.requestType, 'placeSearch:', data.placeSearch, 'webSearch:', data.webSearch, 'tasks count:', data.tasks?.length)

      // モバイルデバッグ用：AI判定結果を一時的に画面表示
      if (data.requestType === 'placeSearch' && data.placeSearch) {
        console.log('[DEBUG] Place search detected by AI:', data.placeSearch)
      } else if (data.requestType === 'webSearch' && data.webSearch) {
        console.log('[DEBUG] Web search detected by AI:', data.webSearch)
      } else {
        console.log('[DEBUG] Normal tasks detected, count:', data.tasks?.length)
      }

      // webSearchの場合はバックエンドで既に処理済みなのでそのまま表示
      if (data.requestType === 'webSearch' && data.tasks && data.tasks.length > 0) {
        console.log('[webSearch] Using backend-generated tasks')
        setVoiceResponse(data)
        await loadUsageCount()
        setTextInput('')
        setIsProcessing(false)
        setProcessingStep(null)
        return
      }

      // placeSearchの自動検出（AIが生成しなかった場合のフォールバック）
      // 重要: バックエンドのAI判定を優先し、summary（AI処理済み）がある場合はフォールバックしない
      let placeSearch = data.placeSearch
      const hasAIProcessed = data.summary && data.summary.length > 0
      console.log('[processText] placeSearch detection check:', { placeSearch, tasksCount: data.tasks.length, hasAIProcessed, transcription: data.transcription, text })
      if (!placeSearch && data.tasks.length === 0 && !hasAIProcessed && (data.transcription || text)) {
        // 全角数字を半角に変換
        const normalizeText = (str: string) => {
          return str.replace(/[０-９]/g, (s) => String.fromCharCode(s.charCodeAt(0) - 0xFEE0))
        }

        const inputText = normalizeText((data.transcription || text).toLowerCase())
        console.log('Checking for place search patterns in:', inputText)

        // 場所検索のパターンを検出（具体的なパターンを先に評価）
        const placePatterns = [
          // 地名指定パターン（優先）
          /(.+?)(周辺|近く)の(.+?)(\d+)選/,  // "秋葉原周辺のおいしいラーメン店10選"
          /(.+?)(周辺|近く)の(.+?)(を|近い順に|遠い順に)(\d+)(つ|個|件)/,  // "この周辺のゴルフ練習場を近い順に5つ"
          /(.+?)(周辺|近く)の(.+?)(を探して|探して|見つけて|リスト)/,  // "秋葉原周辺のカフェを探して"
          /(.+?)の(.+?)(\d+)選/,  // "渋谷の美容院5選"
          /(.+?)の(.+?)(おすすめ|を探して|探して|見つけて|リスト)/,  // "渋谷の美容院おすすめ"
          // 現在地パターン（後に評価）
          /^(周辺|近く|この周辺)で(.+?)(\d+)選/,  // "周辺でおいしいラーメン屋さん5選"
          /^(周辺|近く|この周辺)の(.+?)(\d+)選/,  // "周辺のおいしいラーメン屋さん5選"
          /^(周辺|近く|この周辺)の(.+?)(を|近い順に|遠い順に)(\d+)(つ|個|件)/,  // "この周辺のゴルフ練習場を近い順に5つ"
          /^(周辺|近く|この周辺)で(.+?)(を探して|探して|見つけて|おすすめ|リスト)/,  // "周辺でカフェを探して"
          /^(周辺|近く|この周辺)の(.+?)(を探して|探して|見つけて|おすすめ|リスト)/,  // "周辺のカフェを探して"
          /(.+?)(を探して|探して|見つけて)$/  // "カフェを探して"
        ]

        for (const pattern of placePatterns) {
          const match = inputText.match(pattern)
          if (match) {
            console.log('Pattern matched:', pattern.source, 'Match:', match)
            let location = ''
            let query = ''
            let maxResults = 10

            // パターン別に処理
            if (pattern.source.startsWith('^') && pattern.source.includes('(周辺|近く|この周辺)')) {
              // "周辺でおいしいラーメン屋さん5選" or "この周辺のゴルフ練習場を近い順に5つ"
              location = ''  // 現在地を使用

              if (pattern.source.includes('(を|近い順に|遠い順に)')) {
                // "この周辺のゴルフ練習場を近い順に5つ"
                query = match[2].trim()
                maxResults = match[4] ? parseInt(match[4]) : 10
              } else if (pattern.source.includes('\\d+選')) {
                // "周辺のおいしいラーメン屋さん5選"
                query = match[2].trim()
                maxResults = match[3] ? parseInt(match[3]) : 10
              } else {
                // "周辺でカフェを探して"
                query = match[2].trim()
              }
            } else if (pattern.source.includes('(.+?)(周辺|近く)')) {
              // "秋葉原周辺のおいしいラーメン店10選" or "伊勢神宮周辺の観光スポット10選"
              location = match[1].trim()

              // 「この」で始まる場合は現在地として扱う
              if (location === 'この' || location === 'ここ') {
                location = ''
              }

              query = match[3].trim()

              if (pattern.source.includes('(を|近い順に|遠い順に)')) {
                // "秋葉原周辺のゴルフ練習場を近い順に5つ"
                maxResults = match[5] ? parseInt(match[5]) : 10
              } else if (pattern.source.includes('\\d+選')) {
                // "秋葉原周辺のおいしいラーメン店10選"
                maxResults = match[4] ? parseInt(match[4]) : 10
              }
            } else if (pattern.source.includes('の(.+?)\\d+選')) {
              // "渋谷の美容院5選"
              location = match[1].trim()
              query = match[2].trim()
              maxResults = match[3] ? parseInt(match[3]) : 10
            } else if (pattern.source.includes('の(.+?)(おすすめ|を探して')) {
              // "渋谷の美容院おすすめ"
              location = match[1].trim()
              query = match[2].trim()
            } else {
              // "カフェを探して"
              query = match[1].trim()
            }

            console.log('Auto-detected place search:', { query, location, maxResults })
            placeSearch = { query, location, maxResults }
            break
          }
        }
      }

      // placeSearchがある場合、Google Places APIを呼び出す
      if (placeSearch) {
        console.log('[processText] Place search detected:', placeSearch)

        const { query, location, maxResults } = placeSearch

        // locationが空の場合、位置情報を取得（タイムアウト3秒）
        let searchLocation = location
        if (!location) {
          console.log('[processText] No location specified, trying to get current location...')
          const isNative = Capacitor.isNativePlatform()

          if (isNative) {
            // ネイティブアプリ: navigator.geolocationが動作しない場合があるため、デフォルト座標を使用
            // TODO: @capacitor/geolocationプラグインを導入すればネイティブでも位置情報取得可能
            console.log('[processText] Native platform detected, using default location')
            searchLocation = '35.6812,139.7671' // 東京駅の座標
          } else {
            // Web版: navigator.geolocationを使用
            try {
              // タイムアウト付きで位置情報を取得
              const position = await Promise.race([
                new Promise<GeolocationPosition>((resolve, reject) => {
                  if (!navigator.geolocation) {
                    reject(new Error('Geolocation not available'))
                    return
                  }
                  navigator.geolocation.getCurrentPosition(resolve, reject, {
                    timeout: 3000,
                    enableHighAccuracy: false, // 高精度不要で速度優先
                    maximumAge: 60000 // 1分以内のキャッシュを使用
                  })
                }),
                new Promise<never>((_, reject) =>
                  setTimeout(() => reject(new Error('Geolocation timeout')), 3000)
                )
              ])
              searchLocation = `${position.coords.latitude},${position.coords.longitude}`
              console.log('[processText] Using current location:', searchLocation)
            } catch (geoError: any) {
              console.warn('[processText] Geolocation error (will proceed without location):', geoError.message)
              // 位置情報取得失敗時は東京駅をデフォルトとして使用
              searchLocation = '35.6812,139.7671' // 東京駅の座標
              console.log('[processText] Using default location (Tokyo Station):', searchLocation)
            }
          }
        }

        console.log('[processText] Searching places:', { query, location: searchLocation, maxResults })

        // Google Places APIを呼び出し
        const { data: placesData, error: placesError } = await supabase.functions.invoke('search-places', {
          body: {
            query,
            location: searchLocation,
            maxResults: maxResults || 10
          }
        })

        if (placesError) {
          console.error('Places API error:', placesError)
          throw placesError
        }

        if (placesData?.error) {
          throw new Error(placesData.error)
        }

        console.log('[processText] Places API results:', placesData)
        console.log('[processText] Places count:', placesData.results?.length)

        // プロジェクトIDの決定：AIがnewProjectsを指定していればそれを使用
        let targetProjectId = projects[0]?.id || 'NEW:場所リスト'
        let targetProjectName = projects[0]?.name || '場所リスト'

        if (data.newProjects && data.newProjects.length > 0) {
          // 新しいプロジェクトが指定されている場合
          targetProjectId = `NEW:${data.newProjects[0].name}`
          targetProjectName = data.newProjects[0].name
          console.log('Using new project from AI:', targetProjectName)
        }

        // 結果をタスク形式に変換
        const tasks = (placesData.results || []).map((place: PlaceResult, index: number) => {
          // おすすめポイントを生成
          const highlights = []
          if (place.rating && place.rating >= 4.0) {
            highlights.push(`⭐高評価 ${place.rating}/5.0`)
          }
          if (place.userRatingsTotal && place.userRatingsTotal > 100) {
            highlights.push(`👥レビュー多数 (${place.userRatingsTotal}件)`)
          }
          if (place.priceLevel) {
            const priceIndicator = '¥'.repeat(place.priceLevel)
            highlights.push(`💰価格帯: ${priceIndicator}`)
          }
          if (place.types && place.types.length > 0) {
            const typeLabels: Record<string, string> = {
              'restaurant': '🍽️レストラン',
              'cafe': '☕カフェ',
              'tourist_attraction': '🗼観光スポット',
              'park': '🌳公園',
              'shopping_mall': '🛍️ショッピング',
              'gym': '💪ジム',
              'hospital': '🏥病院',
              'school': '🎓学校'
            }
            const mainType = place.types[0]
            if (typeLabels[mainType]) {
              highlights.push(typeLabels[mainType])
            }
          }

          // 説明文を生成
          const descriptionParts = []

          // AI生成の充実した説明
          if (place.aiEnrichedDescription) {
            descriptionParts.push(`✨ ${place.aiEnrichedDescription}`)
          }

          // 住所
          if (place.address) {
            descriptionParts.push(`📍 ${place.address}`)
          }

          // 評価
          if (place.rating) {
            descriptionParts.push(`⭐ 評価: ${place.rating}/5.0 (${place.userRatingsTotal || 0}件のレビュー)`)
          }

          // 公式サイトとGoogle Mapsリンク
          if (place.websiteUri) {
            descriptionParts.push(`🌐 [公式サイト](${place.websiteUri})`)
          }

          if (place.googleMapsUri) {
            descriptionParts.push(`🗺️ [Google Mapsで見る](${place.googleMapsUri})`)
          }

          // ハイライト（簡潔に）
          if (highlights.length > 0) {
            highlights.forEach(h => descriptionParts.push(h))
          }

          // 電話番号
          if (place.phoneNumber) {
            descriptionParts.push(`📞 ${place.phoneNumber}`)
          }

          const description = descriptionParts.filter(Boolean).join('\n')

          return {
            title: `${index + 1}. ${place.name}`,
            description,
            priority: 'medium',
            projectId: targetProjectId,
            projectName: targetProjectName
          }
        })

        setVoiceResponse({
          transcription: text,
          tasks: tasks,
          summary: data.summary || `${query}を${tasks.length}件見つけました`,
          newProjects: data.newProjects || []
        })
      } else {
        // 通常のタスク
        setVoiceResponse(data)
      }

      await loadUsageCount() // 利用回数を更新
      setTextInput('') // 入力をクリア

    } catch (err: any) {
      console.error('Error processing text:', err)
      const errorMessage = err.message || err.error || 'テキストの処理に失敗しました'
      setError(errorMessage)
    } finally {
      setIsProcessing(false)
      setProcessingStep(null)
    }
  }

  const handleConfirmTasks = async (overrideProjectId?: string, additionalNewProjects?: Array<{ name: string; description?: string; color?: string }>) => {
    if (!voiceResponse) return

    setIsProcessing(true)
    setError(null)

    try {
      console.log('=== Task Creation Debug ===')
      console.log('Tasks to create:', voiceResponse.tasks)
      console.log('New projects:', voiceResponse.newProjects)
      console.log('Additional new projects:', additionalNewProjects)

      // 1. 新規プロジェクトを作成
      const newProjectMap: Record<string, string> = {}
      const allNewProjects = [
        ...(voiceResponse.newProjects || []),
        ...(additionalNewProjects || [])
      ]

      if (allNewProjects.length > 0) {
        // 既存プロジェクトの最小display_orderを取得
        const { data: minOrderData } = await supabase
          .from('projects')
          .select('display_order')
          .eq('user_id', user?.id)
          .order('display_order', { ascending: true })
          .limit(1)
          .maybeSingle()

        let currentMinOrder = minOrderData?.display_order ?? 0

        for (const newProject of allNewProjects) {
          // 各プロジェクトを最小値-1で作成（最上部に表示）
          const newOrder = currentMinOrder - 1
          currentMinOrder = newOrder // 次のプロジェクトはさらに上に

          const { data: createdProject, error: projectError } = await supabase
            .from('projects')
            .insert({
              name: newProject.name,
              description: newProject.description || null,
              color: newProject.color || '#4F46E5',
              user_id: user?.id,
              is_active: true,
              show_thumbnail: false,
              show_in_all_projects: true,
              display_order: newOrder
            })
            .select()
            .single()

          if (projectError) throw projectError

          newProjectMap[`NEW:${newProject.name}`] = createdProject.id
          console.log(`Created new project: "${newProject.name}" -> ${createdProject.id}`)
        }

        console.log('newProjectMap:', newProjectMap)

        // プロジェクト一覧を再取得して表示に反映
        await fetchProjects()
      }

      // 2. 全タスクの最小display_orderを取得
      const { data: minOrderData, error: minOrderError } = await supabase
        .from('todos')
        .select('display_order')
        .eq('user_id', user?.id)
        .order('display_order', { ascending: true })
        .limit(1)
        .maybeSingle()

      if (minOrderError && minOrderError.code !== 'PGRST116') {
        // PGRST116 = データが見つからない（タスクが1つもない場合）
        throw minOrderError
      }

      let currentMinOrder = minOrderData?.display_order ?? 0

      // 3. タスクを作成（各タスクのprojectIdを使用、またはoverride）
      // タスクを逆順にして、最初のタスクが最上位に来るようにする
      const tasksToCreate = [...voiceResponse.tasks].reverse()

      for (const task of tasksToCreate) {
        let targetProjectId = overrideProjectId || task.projectId
        console.log(`Task "${task.title}" - Original projectId: "${targetProjectId}"`)

        // 新規プロジェクトのIDをマッピング
        if (targetProjectId.startsWith('NEW:')) {
          const mappedId = newProjectMap[targetProjectId]
          console.log(`  Mapping "${targetProjectId}" -> ${mappedId || 'NOT FOUND'}`)

          if (!mappedId) {
            console.error(`  ERROR: No mapping found for "${targetProjectId}"`)
            console.error('  Available keys in newProjectMap:', Object.keys(newProjectMap))
            throw new Error(`新規プロジェクト「${targetProjectId.replace('NEW:', '')}」の作成に失敗しました。プロジェクト名が一致しない可能性があります。`)
          }

          targetProjectId = mappedId
        }

        // 各タスクを最小値-1で作成（最上部に表示）
        const newOrder = currentMinOrder - 1
        currentMinOrder = newOrder // 次のタスクはさらに上に

        console.log(`  Final projectId: ${targetProjectId}`)

        const { data: createdTodo, error: todoError } = await supabase
          .from('todos')
          .insert({
            title: task.title,
            description: task.description || '',
            project_id: targetProjectId,
            status: 'pending',
            priority: task.priority === 'high' ? 3 : task.priority === 'medium' ? 2 : 1,
            start_date: task.startDate || null,
            due_date: task.dueDate || null,
            deleted_at: null,
            thumbnail_image_id: null,
            thumbnail_size: 'small',
            user_id: user?.id,
            display_order: newOrder
          })
          .select()
          .single()

        if (todoError) throw todoError

        // 4. タスクに画像がある場合、todo_imagesに保存
        if (task.images && task.images.length > 0) {
          for (let i = 0; i < task.images.length; i++) {
            const image = task.images[i]
            const { error: imageError } = await supabase
              .from('todo_images')
              .insert({
                todo_id: createdTodo.id,
                user_id: user?.id,
                is_external: true,
                external_url: image.external_url,
                source: image.source,
                photographer: image.photographer,
                photographer_url: image.photographer_url,
                license_name: image.license_name,
                license_url: image.license_url,
                file_name: `${image.source}_${Date.now()}.jpg`,
                display_order: i,
                image_url: null,
                file_size: null
              })

            if (imageError) {
              console.error('Error saving image:', imageError)
              // Continue even if image save fails
            }
          }
        }
      }

      // 「全てのプロジェクト」に切り替え（タスクも自動的に再取得される）
      await setSelectedProjectId(null)

      // プロジェクト別のタスク数を集計
      const projectCounts = voiceResponse.tasks.reduce((acc, task) => {
        const projectId = overrideProjectId || task.projectId
        acc[projectId] = (acc[projectId] || 0) + 1
        return acc
      }, {} as Record<string, number>)

      const projectSummary = Object.entries(projectCounts)
        .map(([projectId, count]) => {
          // 既存プロジェクトから検索
          let projectName = projects.find(p => p.id === projectId)?.name

          // 見つからない場合、新規作成されたプロジェクトか確認
          if (!projectName) {
            const newProjectKey = Object.keys(newProjectMap).find(key => newProjectMap[key] === projectId)
            if (newProjectKey) {
              projectName = newProjectKey.replace('NEW:', '')
            }
          }

          // それでも見つからない場合はスキップ（表示しない）
          if (!projectName) {
            return null
          }

          return `${projectName}: ${count}件`
        })
        .filter(Boolean) // nullを除外
        .join(', ')

      // 成功メッセージを表示してリセット
      const newProjectsMessage = voiceResponse.newProjects && voiceResponse.newProjects.length > 0
        ? `\n新規プロジェクト: ${voiceResponse.newProjects.map(p => p.name).join(', ')}`
        : ''
      alert(`${voiceResponse.tasks.length}件のタスクを作成しました！${newProjectsMessage}\n${projectSummary}`)

      // 状態をリセット
      setVoiceResponse(null)
      setError(null)
      setShowModal(false) // モーダルを閉じる
      console.log('Tasks created successfully, modal should close')

    } catch (err: any) {
      console.error('Error creating tasks:', err)
      setError(err.message || 'タスクの作成に失敗しました')
      // エラー時はモーダルを閉じない（エラーメッセージを表示）
    } finally {
      setIsProcessing(false)
    }
  }

  const handleCancel = () => {
    setVoiceResponse(null)
    setError(null)
    setShowModal(false)
  }

  // コンパクト表示（ボタンのみ）
  if (compact) {
    return (
      <>
        <button
          onClick={() => {
            if (usageCount >= usageLimit) {
              setError('月間の利用上限に達しました')
              setShowModal(true)
              return
            }
            setShowModal(true)
          }}
          disabled={isRecording || isProcessing}
          className="relative flex flex-col items-center justify-center w-8 h-8 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-purple-500 via-pink-500 to-red-500 hover:from-purple-600 hover:via-pink-600 hover:to-red-600 text-white shadow-md sm:shadow-lg hover:shadow-xl transition-all duration-300 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed group"
          data-tour="voice-input"
        >
          {/* グロー効果 */}
          <div className="absolute inset-0 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 opacity-0 group-hover:opacity-50 blur-md transition-opacity duration-300"></div>

          {isRecording ? (
            <Square className="h-4 w-4 sm:h-6 sm:w-6 relative z-10" />
          ) : isProcessing ? (
            <Loader2 className="h-4 w-4 sm:h-6 sm:w-6 animate-spin relative z-10" />
          ) : (
            <div className="relative z-10 flex flex-col items-center">
              <Mic className="h-3.5 w-3.5 sm:h-5 sm:w-5 sm:mb-0.5" />
              <span className="hidden sm:block text-[8px] font-bold leading-none tracking-wider">AI</span>
            </div>
          )}
        </button>

        {/* モーダル */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 space-y-4">
                {/* ヘッダー */}
                <div className="flex items-center justify-between border-b pb-4">
                  <h3 className="font-semibold text-gray-900 text-lg">AI音声アシスタント</h3>
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-gray-600">
                      今月 {usageCount}/{usageLimit}回
                    </span>
                    <button
                      onClick={() => {
                        setShowModal(false)
                        setError(null)
                      }}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X className="h-6 w-6" />
                    </button>
                  </div>
                </div>

                {/* エラー表示 */}
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                    <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-red-700">{error}</span>
                  </div>
                )}

                {/* レスポンス表示 */}
                {voiceResponse && (
                  <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 space-y-3">
                    <div>
                      <h4 className="font-medium text-indigo-900 mb-1">音声認識結果:</h4>
                      <p className="text-sm text-indigo-800">{voiceResponse.transcription}</p>
                    </div>

                    {/* サマリー表示（デバッグ情報を含む） */}
                    {voiceResponse.summary && (
                      <div>
                        <h4 className="font-medium text-indigo-900 mb-1">サマリー:</h4>
                        <p className="text-sm text-indigo-800 whitespace-pre-wrap">{voiceResponse.summary}</p>
                      </div>
                    )}

                    <div>
                      <h4 className="font-medium text-indigo-900 mb-2">作成されるタスク ({voiceResponse.tasks.length}件):</h4>
                      <ul className="space-y-2">
                        {voiceResponse.tasks.map((task, index) => (
                          <li key={index} className="bg-white rounded p-2 text-sm">
                            <div className="w-full">
                              <div className="font-medium text-gray-900 break-words">{task.title}</div>
                              {task.description && (
                                <div className="text-gray-600 text-xs mt-1 pl-2 w-full">
                                  <ReactMarkdown
                                    components={{
                                      a: ({ node, ...props }) => (
                                        <a {...props} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline" />
                                      ),
                                      p: ({ node, ...props }) => (
                                        <p {...props} className="my-1 break-words w-full" />
                                      ),
                                      strong: ({ node, ...props }) => (
                                        <strong {...props} className="font-bold break-words inline" />
                                      )
                                    }}
                                  >
                                    {task.description}
                                  </ReactMarkdown>
                                </div>
                              )}
                              {task.images && task.images.length > 0 && (
                                <div className="flex gap-2 mt-2 overflow-x-auto">
                                  {task.images.slice(0, 3).map((image, imgIndex) => (
                                    <div key={imgIndex} className="relative flex-shrink-0">
                                      <img
                                        src={image.external_url}
                                        alt={`${task.title} - Image ${imgIndex + 1}`}
                                        className="w-16 h-16 object-cover rounded border border-gray-200"
                                      />
                                      <div className="text-[10px] text-gray-500 mt-0.5 truncate w-16">
                                        📷 {image.photographer}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                              <div className="flex items-center gap-2 mt-1 flex-wrap">
                                {task.priority && (
                                  <span className={`inline-block px-2 py-0.5 text-xs rounded ${
                                    task.priority === 'high' ? 'bg-red-100 text-red-700' :
                                    task.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                                    'bg-gray-100 text-gray-700'
                                  }`}>
                                    {task.priority === 'high' ? '高' : task.priority === 'medium' ? '中' : '低'}
                                  </span>
                                )}
                                <div className="flex items-center gap-1">
                                  <div
                                    className="w-2 h-2 rounded-full"
                                    style={{ backgroundColor: projects.find(p => p.id === task.projectId)?.color || '#6366F1' }}
                                  />
                                  <span className="text-xs text-gray-500">
                                    {task.projectName || projects.find(p => p.id === task.projectId)?.name || '不明'}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* プロジェクト選択セクション */}
                    <div className="border-t border-indigo-200 pt-3">
                      <h4 className="font-medium text-indigo-900 mb-2">タスクの作成方法を選択:</h4>

                      <div className="flex flex-col gap-2">
                        {/* 1. AIが振り分けたプロジェクトに作成 */}
                        <button
                          onClick={() => handleConfirmTasks()}
                          disabled={isProcessing}
                          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                        >
                          <Check className="h-4 w-4" />
                          <span className="font-medium">AIが振り分けたプロジェクトに作成</span>
                        </button>

                        {/* 2. 既存プロジェクトを選択 */}
                        <div className="flex gap-2">
                          <select
                            value={selectedProjectOverride || ''}
                            onChange={(e) => setSelectedProjectOverride(e.target.value || null)}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          >
                            <option value="">{t('project.selectExisting')}</option>
                            {projects.map(project => (
                              <option key={project.id} value={project.id}>
                                {project.name}
                              </option>
                            ))}
                          </select>
                          <button
                            onClick={() => {
                              if (selectedProjectOverride) {
                                handleConfirmTasks(selectedProjectOverride)
                              }
                            }}
                            disabled={!selectedProjectOverride || isProcessing}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                          >
                            作成
                          </button>
                        </div>

                        {/* 3. 新規プロジェクトを作成 */}
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={newProjectName}
                            onChange={(e) => setNewProjectName(e.target.value)}
                            placeholder="新規プロジェクト名を入力..."
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          />
                          <button
                            onClick={async () => {
                              if (newProjectName.trim()) {
                                // 新規プロジェクトを作成してからタスクを作成
                                const newProjectId = `NEW:${newProjectName.trim()}`

                                // 新規プロジェクト情報を直接渡す
                                const additionalProject = {
                                  name: newProjectName.trim(),
                                  description: `${newProjectName.trim()}に関するタスク`,
                                  color: '#6366F1'
                                }

                                await handleConfirmTasks(newProjectId, [additionalProject])
                              }
                            }}
                            disabled={!newProjectName.trim() || isProcessing}
                            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                          >
                            作成
                          </button>
                        </div>

                        {/* 4. 一時保存に作成 */}
                        {tempStorageProjectId && (
                          <button
                            onClick={() => handleConfirmTasks(tempStorageProjectId)}
                            disabled={isProcessing}
                            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                          >
                            <span className="font-medium">すべて一時保存に作成</span>
                            <span className="text-xs opacity-80">(後で整理)</span>
                          </button>
                        )}

                        {/* 5. キャンセル */}
                        <button
                          onClick={handleCancel}
                          disabled={isProcessing}
                          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors"
                        >
                          <X className="h-4 w-4" />
                          <span className="font-medium">キャンセル</span>
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* 録音コントロール */}
                {!voiceResponse && (
                  <div className="flex flex-col items-center gap-3 py-6">
                    {isProcessing ? (
                      <div className="flex flex-col items-center gap-3 py-4">
                        <Loader2 className="h-12 w-12 text-indigo-600 animate-spin" />
                        {processingStep === 'transcribing' ? (
                          <div className="text-center">
                            <p className="text-sm font-medium text-gray-700">🎤 音声を認識中...</p>
                            <p className="text-xs text-gray-500 mt-1">Whisper APIで音声をテキスト化しています</p>
                          </div>
                        ) : processingStep === 'analyzing' ? (
                          <div className="text-center">
                            <p className="text-sm font-medium text-gray-700">🤖 タスクを解析中...</p>
                            <p className="text-xs text-gray-500 mt-1">AIがタスクを抽出しています</p>
                          </div>
                        ) : (
                          <p className="text-sm text-gray-600">音声を処理中...</p>
                        )}
                        {/* プログレスバー */}
                        <div className="w-full max-w-xs">
                          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-indigo-600 transition-all duration-500"
                              style={{
                                width: processingStep === 'transcribing' ? '50%' :
                                       processingStep === 'analyzing' ? '90%' : '10%'
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    ) : isRecording ? (
                      <>
                        <div className="flex flex-col items-center gap-2">
                          <div className="relative">
                            <div className="absolute inset-0 bg-red-500 rounded-full animate-ping opacity-75"></div>
                            <button
                              onClick={stopRecording}
                              className="relative flex items-center justify-center w-20 h-20 bg-red-500 hover:bg-red-600 rounded-full text-white shadow-lg transition-colors"
                            >
                              <Square className="h-10 w-10" />
                            </button>
                          </div>
                          <p className="text-sm font-medium text-gray-700">
                            録音中... {recordingTime}秒 / 60秒
                          </p>
                          {/* リアルタイム認識結果の表示（ネイティブアプリのみ） */}
                          {transcriptionText && Capacitor.isNativePlatform() && (
                            <div className="w-full max-w-md mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                              <p className="text-xs text-blue-600 mb-1">認識中:</p>
                              <p className="text-sm text-gray-800">{transcriptionText}</p>
                            </div>
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        {/* テキスト入力エリア */}
                        <div className="w-full max-w-md space-y-3">
                          <textarea
                            value={textInput}
                            onChange={(e) => setTextInput(e.target.value)}
                            placeholder="タスク内容を入力...（例: 京都の観光地を調べて）"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
                            rows={3}
                            disabled={usageCount >= usageLimit}
                          />
                          {textInput.trim() && (
                            <button
                              onClick={() => processText(textInput.trim())}
                              disabled={usageCount >= usageLimit || !textInput.trim()}
                              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                            >
                              <span className="font-medium">送信</span>
                            </button>
                          )}
                        </div>

                        {/* 区切り線 */}
                        <div className="flex items-center gap-3 w-full max-w-md">
                          <div className="flex-1 border-t border-gray-300"></div>
                          <span className="text-sm text-gray-500">または</span>
                          <div className="flex-1 border-t border-gray-300"></div>
                        </div>

                        {/* マイクボタン */}
                        <button
                          onClick={startRecording}
                          disabled={usageCount >= usageLimit}
                          className="flex items-center justify-center w-20 h-20 bg-indigo-600 hover:bg-indigo-700 rounded-full text-white shadow-lg transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                        >
                          <Mic className="h-10 w-10" />
                        </button>
                        <p className="text-sm text-gray-600 text-center">
                          音声でタスクを追加<br />
                          <span className="text-xs text-gray-500">（最大60秒）</span>
                        </p>
                      </>
                    )}
                  </div>
                )}

                {/* 使い方 */}
                {!voiceResponse && !isRecording && !isProcessing && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-2">
                    <div>
                      <h4 className="text-xs font-semibold text-gray-700 mb-1">使い方:</h4>
                      <ul className="text-xs text-gray-600 space-y-1">
                        <li>1. マイクボタンを押して録音開始</li>
                        <li>2. タスク内容を話す（例: 「企画書を作成、デザイン案を3つ考える」）</li>
                        <li>3. 停止ボタンを押すとAIが自動でタスクを作成</li>
                      </ul>
                    </div>

                    <div className="border-t border-gray-300 pt-2">
                      <h4 className="text-xs font-semibold text-gray-700 mb-1">💡 便利なキーワード:</h4>
                      <ul className="text-xs text-gray-600 space-y-1">
                        <li><strong>「ステップで」「手順を」</strong> → 複数タスクに分割</li>
                        <li className="pl-3 text-gray-500">例: 「本格カレーの作り方をステップで」</li>
                        <li><strong>「調べて」「リストアップ」</strong> → 候補を個別タスク化</li>
                        <li className="pl-3 text-gray-500">例: 「京都の観光地を調べて」→ 各スポットが個別タスクに</li>
                        <li className="text-gray-500">※キーワードなし → 1つのタスクとして登録</li>
                        <li className="text-gray-500">※ドラッグ&ドロップで順序変更可能</li>
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </>
    )
  }

  // フル表示（従来のUI）
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
      {/* ヘッダー */}
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-gray-900">AI音声アシスタント</h3>
        <span className="text-sm text-gray-600">
          今月 {usageCount}/{usageLimit}回
        </span>
      </div>

      {/* エラー表示 */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
          <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <span className="text-sm text-red-700">{error}</span>
        </div>
      )}

      {/* レスポンス表示 */}
      {voiceResponse && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 space-y-3">
          <div>
            <h4 className="font-medium text-indigo-900 mb-1">音声認識結果:</h4>
            <p className="text-sm text-indigo-800">{voiceResponse.transcription}</p>
          </div>

          <div>
            <h4 className="font-medium text-indigo-900 mb-2">作成されるタスク ({voiceResponse.tasks.length}件):</h4>
            <ul className="space-y-2">
              {voiceResponse.tasks.map((task, index) => (
                <li key={index} className="bg-white rounded p-2 text-sm">
                  <div className="w-full">
                    <div className="font-medium text-gray-900">{task.title}</div>
                    {task.description && (
                      <div className="text-gray-600 text-xs mt-1 pl-2 w-full">
                        <ReactMarkdown
                          components={{
                            a: ({ node, ...props }) => (
                              <a {...props} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline" />
                            ),
                            p: ({ node, ...props }) => (
                              <p {...props} className="my-1 break-words w-full" />
                            ),
                            strong: ({ node, ...props }) => (
                              <strong {...props} className="font-bold break-words inline" />
                            )
                          }}
                        >
                          {task.description}
                        </ReactMarkdown>
                      </div>
                    )}
                    {task.images && task.images.length > 0 && (
                      <div className="flex gap-2 mt-2 overflow-x-auto">
                        {task.images.slice(0, 3).map((image, imgIndex) => (
                          <div key={imgIndex} className="relative flex-shrink-0">
                            <img
                              src={image.external_url}
                              alt={`${task.title} - Image ${imgIndex + 1}`}
                              className="w-16 h-16 object-cover rounded border border-gray-200"
                            />
                            <div className="text-[10px] text-gray-500 mt-0.5 truncate w-16">
                              📷 {image.photographer}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      {task.priority && (
                        <span className={`inline-block px-2 py-0.5 text-xs rounded ${
                          task.priority === 'high' ? 'bg-red-100 text-red-700' :
                          task.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {task.priority === 'high' ? '高' : task.priority === 'medium' ? '中' : '低'}
                        </span>
                      )}
                      <div className="flex items-center gap-1">
                        <div
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: projects.find(p => p.id === task.projectId)?.color || '#6366F1' }}
                        />
                        <span className="text-xs text-gray-500">
                          {task.projectName || projects.find(p => p.id === task.projectId)?.name || '不明'}
                        </span>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* プロジェクト選択セクション */}
          <div className="border-t border-indigo-200 pt-3">
            <h4 className="font-medium text-indigo-900 mb-2">タスクの作成方法を選択:</h4>

            <div className="flex flex-col gap-2">
              {/* 1. AIが振り分けたプロジェクトに作成 */}
              <button
                onClick={() => handleConfirmTasks()}
                disabled={isProcessing}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                <Check className="h-4 w-4" />
                <span className="font-medium">AIが振り分けたプロジェクトに作成</span>
              </button>

              {/* 2. 一時保存に作成 */}
              {tempStorageProjectId && (
                <button
                  onClick={() => handleConfirmTasks(tempStorageProjectId)}
                  disabled={isProcessing}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                >
                  <span className="font-medium">すべて一時保存に作成</span>
                  <span className="text-xs opacity-80">(後で整理)</span>
                </button>
              )}

              {/* 3. キャンセル */}
              <button
                onClick={handleCancel}
                disabled={isProcessing}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors"
              >
                <X className="h-4 w-4" />
                <span className="font-medium">キャンセル</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 録音コントロール */}
      {!voiceResponse && (
        <div className="flex flex-col items-center gap-3">
          {isProcessing ? (
            <div className="flex flex-col items-center gap-2 py-4">
              <Loader2 className="h-8 w-8 text-indigo-600 animate-spin" />
              <p className="text-sm text-gray-600">音声を解析中...</p>
            </div>
          ) : isRecording ? (
            <>
              <div className="flex flex-col items-center gap-2">
                <div className="relative">
                  <div className="absolute inset-0 bg-red-500 rounded-full animate-ping opacity-75"></div>
                  <button
                    onClick={stopRecording}
                    className="relative flex items-center justify-center w-16 h-16 bg-red-500 hover:bg-red-600 rounded-full text-white shadow-lg transition-colors"
                  >
                    <Square className="h-8 w-8" />
                  </button>
                </div>
                <p className="text-sm font-medium text-gray-700">
                  録音中... {recordingTime}秒 / 60秒
                </p>
                {/* リアルタイム認識結果の表示（ネイティブアプリのみ） */}
                {transcriptionText && Capacitor.isNativePlatform() && (
                  <div className="w-full mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-xs text-blue-600 mb-1">認識中:</p>
                    <p className="text-sm text-gray-800">{transcriptionText}</p>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              {/* テキスト入力エリア */}
              <div className="w-full space-y-3">
                <textarea
                  value={textInput}
                  onChange={(e) => setTextInput(e.target.value)}
                  placeholder="タスク内容を入力...（例: 京都の観光地を調べて）"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
                  rows={3}
                  disabled={usageCount >= usageLimit}
                />
                {textInput.trim() && (
                  <button
                    onClick={() => processText(textInput.trim())}
                    disabled={usageCount >= usageLimit || !textInput.trim()}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                  >
                    <span className="font-medium">送信</span>
                  </button>
                )}
              </div>

              {/* 区切り線 */}
              <div className="flex items-center gap-3 w-full">
                <div className="flex-1 border-t border-gray-300"></div>
                <span className="text-sm text-gray-500">または</span>
                <div className="flex-1 border-t border-gray-300"></div>
              </div>

              {/* マイクボタン */}
              <button
                onClick={startRecording}
                disabled={usageCount >= usageLimit}
                className="flex items-center justify-center w-16 h-16 bg-indigo-600 hover:bg-indigo-700 rounded-full text-white shadow-lg transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                <Mic className="h-8 w-8" />
              </button>
              <p className="text-sm text-gray-600 text-center">
                音声でタスクを追加<br />
                <span className="text-xs text-gray-500">（最大60秒）</span>
              </p>
            </>
          )}
        </div>
      )}

      {/* 使い方 */}
      {!voiceResponse && !isRecording && !isProcessing && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-2">
          <div>
            <h4 className="text-xs font-semibold text-gray-700 mb-1">使い方:</h4>
            <ul className="text-xs text-gray-600 space-y-1">
              <li>1. マイクボタンを押して録音開始</li>
              <li>2. タスク内容を話す（例: 「企画書を作成、デザイン案を3つ考える」）</li>
              <li>3. 停止ボタンを押すとAIが自動でタスクを作成</li>
            </ul>
          </div>

          <div className="border-t border-gray-300 pt-2">
            <h4 className="text-xs font-semibold text-gray-700 mb-1">💡 便利なキーワード:</h4>
            <ul className="text-xs text-gray-600 space-y-1">
              <li><strong>「ステップで」「手順を」</strong> → 複数タスクに分割</li>
              <li className="pl-3 text-gray-500">例: 「本格カレーの作り方をステップで」</li>
              <li><strong>「調べて」「リストアップ」</strong> → 候補を個別タスク化</li>
              <li className="pl-3 text-gray-500">例: 「京都の観光地を調べて」→ 各スポットが個別タスクに</li>
              <li className="text-gray-500">※キーワードなし → 1つのタスクとして登録</li>
              <li className="text-gray-500">※ドラッグ&ドロップで順序変更可能</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  )
}
